// @ts-strict-ignore
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Avatar from 'antd/lib/avatar/avatar';
import Button from 'antd/lib/button';
import Col from 'antd/lib/col';
import Divider from 'antd/lib/divider';
import Progress from 'antd/lib/progress';
import Row from 'antd/lib/row';
import Title from 'antd/lib/typography/Title';
import Text from 'antd/lib/typography/Text';
import Icon, { CheckCircleFilled, ArrowRightOutlined, CloseOutlined } from '@ant-design/icons';
import { AppRouteComponentMatchParams, SetupStep, SetupStepType } from '../../typescriptHelpers';
import { displayTextForAppPlatform } from '../../helpers/displayHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { RootState } from '../../reducers/reducers';
import { selectAppPlatform, selectAppSettings, selectAppStoreType } from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { AppPlatform, AppStoreType } from '../../types';
import { createSetupStepRoute } from '../../helpers/routeHelpers';
import appPlaceholderImage from '../../../assets/app-placeholder.png';
import { trackOnboardingEvent } from '../../actions/trackEvent';
import { Event } from '../../utils/analytics';
import { CustomIconComponentProps } from '@ant-design/icons/lib/components/Icon';
import { ComponentType } from 'react';

const MIN_COL_WIDTH = '100%';

interface TaskListProps {
    appId: string;
    stepsSequence: Record<SetupStepType, SetupStep>;
    completedSteps: string[];
    progress: number;
    onDismiss: () => void;
    shouldHideHeader?: boolean;
}

const TaskList = ({
    appId,
    stepsSequence,
    completedSteps,
    progress,
    onDismiss,
    shouldHideHeader,
}: TaskListProps): JSX.Element => {
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const { appIdParam, orgIdParam } = useParams<AppRouteComponentMatchParams>();
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, appId));

    return (
        <Row gutter={[0, 24]}>
            {!shouldHideHeader && (
                <>
                    <Col span={24} style={{ minWidth: MIN_COL_WIDTH }}>
                        <TaskListHeader
                            appName={appSettings.appName}
                            appImageUrl={appSettings.appImageUrl}
                            appPlatform={appSettings.platform}
                            appStoreType={appSettings.appStoreType}
                            onDismiss={onDismiss}
                        />
                    </Col>
                    <Col span={16} style={{ minWidth: MIN_COL_WIDTH }}>
                        <Progress percent={progress} showInfo={false} />
                    </Col>
                </>
            )}
            <Col span={16} style={{ minWidth: MIN_COL_WIDTH }}>
                <div
                    style={{
                        paddingTop: 24,
                        paddingBottom: 24,
                        paddingLeft: 36,
                        paddingRight: 36,
                        border: `1px solid ${antGrey.grey5}`,
                        borderRadius: 8,
                        boxShadow: 'var(--rw-shadow-card)',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {Object.entries(stepsSequence).map(([type, step], idx) => {
                        const isStepComplete = completedSteps.includes(type);
                        const stepRoute = createSetupStepRoute(orgIdParam, appIdParam, type);
                        const title = typeof step.title === 'string' ? step.title : step.title(platform, appStoreType);
                        const icon = step.icon(platform);
                        return (
                            <React.Fragment key={title}>
                                <TaskListStep
                                    appId={appId}
                                    orgId={orgIdParam}
                                    stepType={type}
                                    isComplete={isStepComplete}
                                    icon={icon}
                                    title={step.listTitle}
                                    description={step.listDescription}
                                    stepUrl={stepRoute}
                                />
                                {idx !== Object.values(stepsSequence).length - 1 && (
                                    <Divider type="horizontal" style={{ borderColor: antGrey.grey5 }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </Col>
        </Row>
    );
};

const TaskListHeader: React.FC<{
    appName: string;
    appImageUrl: string;
    appPlatform: AppPlatform;
    appStoreType?: AppStoreType;
    onDismiss: () => void;
}> = ({ appName, appImageUrl, appPlatform, appStoreType, onDismiss }) => {
    const displayPlatform = displayTextForAppPlatform(appPlatform, appStoreType);
    return (
        <div style={{ display: 'flex', alignContent: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignContent: 'center', gap: 26 }}>
                <Avatar
                    style={{
                        borderRadius: 12,
                        minWidth: 64,
                        filter: 'var(--rw-filter-drop-shadow)',
                    }}
                    src={appImageUrl || appPlaceholderImage}
                    size={64}
                />
                <Title
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 0,
                        fontSize: 24,
                        fontWeight: 600,
                    }}
                >
                    <span>
                        Let’s set up&nbsp;
                        {
                            <span style={{ color: runwayColors.info }}>
                                {appName} ({displayPlatform})
                            </span>
                        }
                        &nbsp;in Runway.
                    </span>
                </Title>
            </div>
            <Button
                icon={<CloseOutlined style={{ color: antGrey.grey6, fontSize: 28 }} />}
                onClick={onDismiss}
                type="link"
            />
        </div>
    );
};

const TaskListStep: React.FC<{
    orgId: string;
    appId: string;
    stepType: string;
    isComplete: boolean;
    icon: ComponentType<CustomIconComponentProps>;
    title: string;
    description: string;
    stepUrl: string;
}> = ({ appId, orgId, stepType, isComplete, icon, title, description, stepUrl }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const iconColor = isComplete ? runwayColors.statusGreen : antGrey.grey4;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
            <Icon component={CheckCircleFilled} style={{ color: `${iconColor}`, fontSize: 30 }} />
            <Icon component={icon} style={{ color: `${antGrey.grey5}`, fontSize: 60 }} />
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Text style={{ color: `${antGrey.grey9}`, fontSize: 20 }}>{title}</Text>
                <Text style={{ color: `${antGrey.grey7}` }}>{description}</Text>
            </div>
            <Button
                type="primary"
                size="large"
                onClick={() => {
                    dispatch(
                        trackOnboardingEvent({
                            orgId,
                            appId,
                            taskType: stepType,
                            eventName: Event.ClickOnboardingTaskCta,
                        }),
                    );
                    history.push(stepUrl);
                }}
                icon={<ArrowRightOutlined />}
                // style={{ color: 'white' }}
            >
                Go
            </Button>
        </div>
    );
};

export default TaskList;
